import { Link } from "react-router-dom";
import { BRAND } from "@/lib/brand";

/**
 * Feb 2026 — Brand logo. Reads its src + alt text from `BRAND`
 * (see `/lib/brand.js`) so the entire site's logo can be swapped
 * by editing one file.
 *
 * variants:
 *   default: links to /, shows full logo on a transparent (light) background
 *   dark   : wraps in a white pill so the logo (which uses dark navy text) is legible on dark backgrounds
 *   plain  : non-clickable (e.g. in emails or headers that are already wrapped in <a>)
 */
export function BrandLogo({ to = "/", variant = "default", className = "", size = "md" }) {
  // Feb 2026 — the new horizontal AWARE Water Testing wordmark is
  // ~3:1 aspect ratio, so we bump heights slightly vs. the old squarer
  // SCWT logo. h-12 at md gives ~144px wide which reads well in the
  // header without pushing the nav out of alignment.
  const heights = { sm: "h-10", md: "h-12", lg: "h-16" };
  const h = heights[size] || heights.md;

  const img = (
    <img
      src={BRAND.logos.primary}
      alt={BRAND.name}
      className={`${h} w-auto object-contain ${className}`}
      draggable={false}
    />
  );

  if (variant === "dark") {
    // The new AWARE wordmark uses blue text on transparent, which is
    // hard to read on a dark-navy footer background. Wrap in a white
    // pill so contrast is preserved.
    const inner = (
      <div className="inline-flex items-center bg-white rounded-xl px-3 py-1.5 shadow-sm">
        {img}
      </div>
    );
    if (!to) return inner;
    return <Link to={to} className="inline-block group" data-testid="brand-logo">{inner}</Link>;
  }

  if (variant === "plain" || !to) return img;

  return (
    <Link to={to} className="inline-flex items-center group" data-testid="brand-logo">
      {img}
    </Link>
  );
}
